import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';

import {
  botSipRoutingAdd,
  botSipRoutingUpdate,
  callexAiAgentOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { allowIpListGet } from '../../../store/slices/tenantSlices/settingSlice';

const AddEditBotSipRoutingModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [botOptions, setBotOptions] = useState([]);
  const [allowIpOptions, setAllowIpOptions] = useState([]);

  const initialValues = {
    allow_ip_id: '',
    bot_id: '',
    prefix: ''
  };

  const validationSchema = Yup.object({
    allow_ip_id: Yup.mixed().required('Allow IP is required'),
    bot_id: Yup.mixed().required('Bot is required'),
    prefix: Yup.string().trim().required('Prefix is required')
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      const body = {
        allow_ip_id: values.allow_ip_id,
        bot_id: values.bot_id,
        prefix: values.prefix.trim()
      };
      const action = isEdit
        ? botSipRoutingUpdate({ id: editRecord.id, ...body })
        : botSipRoutingAdd(body);
      const response = await dispatch(action).unwrap();
      if (response && response.success) {
        toast.success(
          response.message || (isEdit ? 'Record Updated Successfully' : 'Record Added Successfully')
        );
        onCloseClick(true);
        resetForm();
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getBotOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      setBotOptions(
        response?.data ? response.data.map((val) => ({ label: val.name, value: val.id })) : []
      );
    } catch (error) {
      setBotOptions([]);
      console.log('error', error);
    }
  };

  const getAllowIpOptions = async () => {
    try {
      const response = await dispatch(allowIpListGet({ page: 1, limit: 1000 })).unwrap();
      setAllowIpOptions(
        response?.data
          ? response.data.map((val) => ({
              label: val.description ? `${val.ip} (${val.description})` : val.ip,
              value: val.id
            }))
          : []
      );
    } catch (error) {
      setAllowIpOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBotOptions();
    getAllowIpOptions();
  }, []);

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        allow_ip_id: editRecord.allow_ip_id ?? '',
        bot_id: editRecord.bot_id ?? '',
        prefix: editRecord.prefix || ''
      });
    } else {
      resetForm();
    }
  }, [isEdit, editRecord, isOpen]);

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Bot SIP Routing' : 'Add Bot SIP Routing'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="bot-sip-routing-form" onSubmit={handleSubmit}>
          <Row className="mt-3">
            <Col lg="6">
              <FormSelect
                name="allow_ip_id"
                label="Allow IP"
                placeholder="Select Allow IP"
                value={values.allow_ip_id}
                options={allowIpOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="bot_id"
                label="Bot"
                placeholder="Select Bot"
                value={values.bot_id}
                options={botOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg="6">
              <FormInput
                name="prefix"
                label="Prefix"
                placeholder="e.g. 91"
                value={values.prefix}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="bot-sip-routing-form"
          type="submit"
          className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditBotSipRoutingModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditBotSipRoutingModal;
